import { createFileRoute, Link } from "@tanstack/react-router";
import { requireAdmin } from "@/lib/admin-guard";
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import {
  ArrowLeft,
  ShieldAlert,
  Plus,
  ArrowUpCircle,
  CheckCircle2,
  RotateCcw,
  MessageSquare,
  Clock,
  X,
} from "lucide-react";
import {
  listEscalationCases,
  getEscalationCase,
  createEscalationCase,
  addEscalationNote,
  escalateCase,
  closeEscalationCase,
  reopenEscalationCase,
  SOURCE_TYPES,
  SEVERITIES,
  STAGE_LABEL,
} from "@/lib/escalation-cases.functions";

export const Route = createFileRoute("/admin_/escalation-cases")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "Escalation Cases — Admin" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  beforeLoad: requireAdmin,
  component: EscalationCasesPage,
});

const SOURCE_LABEL: Record<string, string> = {
  flagged_content: "Flagged content",
  reported_post: "Reported post/comment",
  complaint: "User complaint",
  system_alert: "System alert",
  safeguarding: "Safeguarding concern",
  compliance: "Compliance incident",
  other: "Other",
};

const STATUS_STYLES: Record<string, string> = {
  open: "bg-blue-50 text-blue-800 border-blue-200",
  in_review: "bg-amber-50 text-amber-800 border-amber-200",
  escalated: "bg-orange-50 text-orange-800 border-orange-200",
  closed: "bg-emerald-50 text-emerald-800 border-emerald-200",
};

const SEVERITY_STYLES: Record<string, string> = {
  low: "bg-muted text-foreground border-border",
  medium: "bg-blue-50 text-blue-800 border-blue-200",
  high: "bg-orange-50 text-orange-800 border-orange-200",
  critical: "bg-red-50 text-red-800 border-red-200",
};

function Badge({ className, children }: { className: string; children: React.ReactNode }) {
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${className}`}
    >
      {children}
    </span>
  );
}

function fmt(d: string | null | undefined) {
  if (!d) return "—";
  return new Date(d).toLocaleString();
}

function EscalationCasesPage() {
  const qc = useQueryClient();
  const list = useServerFn(listEscalationCases);
  const [showCreate, setShowCreate] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>("all");

  const { data, isLoading } = useQuery({
    queryKey: ["admin", "escalation-cases"],
    queryFn: () => list(),
  });

  const refresh = () => qc.invalidateQueries({ queryKey: ["admin", "escalation-cases"] });

  const cases = (data?.cases ?? []).filter((c: any) =>
    filterStatus === "all" ? true : c.status === filterStatus,
  );

  return (
    <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <Link
          to="/admin"
          className="inline-flex items-center gap-2 text-sm font-medium text-foreground/70 hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Admin
        </Link>
        <Link
          to="/admin/escalation-pathway"
          className="inline-flex items-center gap-2 rounded-lg border border-gold/30 bg-card px-3 py-2 text-sm font-medium text-foreground hover:bg-gold/10"
        >
          <ShieldAlert className="h-4 w-4" />
          View Escalation Pathway
        </Link>
      </div>

      <header className="mb-6 flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-full bg-gold/15 text-[var(--gold-deep)]">
            <ShieldAlert className="h-5 w-5" />
          </div>
          <div>
            <h1 className="font-display text-3xl text-foreground">Escalation Cases</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Log, progress, and close cases through the 3-stage governance pathway. Every action
              is recorded for audit. Stages 1 and 2 are currently both performed by Jana Burkhardt:
              escalating a case raises the governance level and decision-making authority, not the
              person reviewing it. This pathway is non-clinical.
            </p>
          </div>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="inline-flex items-center gap-2 rounded-full bg-gold px-4 py-2 text-sm font-semibold text-white hover:bg-gold/90"
        >
          <Plus className="h-4 w-4" /> New case
        </button>
      </header>

      <div className="mb-4 flex flex-wrap gap-2">
        {["all", "open", "in_review", "escalated", "closed"].map((s) => (
          <button
            key={s}
            onClick={() => setFilterStatus(s)}
            className={`rounded-full border px-3 py-1 text-xs font-medium ${
              filterStatus === s
                ? "border-gold bg-gold text-white"
                : "border-gold/20 bg-card text-foreground hover:bg-gold/10"
            }`}
          >
            {s.replace("_", " ")}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="text-muted-foreground">Loading cases…</div>
      ) : cases.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-gold/30 bg-card p-10 text-center text-sm text-muted-foreground">
          No cases match this filter. Click <span className="font-medium">New case</span> to log one.
        </div>
      ) : (
        <div className="space-y-3">
          {cases.map((c: any) => (
            <button
              key={c.id}
              onClick={() => setOpenId(c.id)}
              className="block w-full rounded-2xl border border-gold/20 bg-card p-5 text-start transition-colors hover:bg-gold/5"
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="font-display text-base text-foreground">{c.title}</h3>
                    <Badge className={STATUS_STYLES[c.status] ?? STATUS_STYLES.open}>
                      {c.status.replace("_", " ")}
                    </Badge>
                    <Badge className={SEVERITY_STYLES[c.severity] ?? SEVERITY_STYLES.medium}>
                      {c.severity}
                    </Badge>
                    <Badge className="bg-card text-foreground border-gold/20">
                      Stage {c.current_stage}: {STAGE_LABEL[c.current_stage]}
                    </Badge>
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {SOURCE_LABEL[c.source_type] ?? c.source_type}
                    {c.source_ref ? ` · ${c.source_ref}` : ""} · Opened {fmt(c.created_at)} ·{" "}
                    {c.action_count} actions
                  </p>
                  {c.description && (
                    <p className="mt-2 line-clamp-2 text-sm text-foreground/80">{c.description}</p>
                  )}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {showCreate && (
        <CreateCaseModal
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false);
            refresh();
          }}
        />
      )}

      {openId && (
        <CaseDetailModal
          caseId={openId}
          onClose={() => setOpenId(null)}
          onChange={refresh}
        />
      )}
    </div>
  );
}

// ============= Create modal =============

function CreateCaseModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}) {
  const create = useServerFn(createEscalationCase);
  const m = useMutation({
    mutationFn: (vars: any) => create({ data: vars }),
    onSuccess: () => {
      toast.success("Case opened");
      onCreated();
    },
    onError: (e: any) => toast.error(e?.message ?? "Failed to create case"),
  });

  const [form, setForm] = useState({
    title: "",
    description: "",
    source_type: "flagged_content" as (typeof SOURCE_TYPES)[number],
    source_ref: "",
    severity: "medium" as (typeof SEVERITIES)[number],
  });

  return (
    <Modal title="New escalation case" onClose={onClose}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (form.title.trim().length < 3) {
            toast.error("Title must be at least 3 characters");
            return;
          }
          m.mutate({
            ...form,
            description: form.description || null,
            source_ref: form.source_ref || null,
          });
        }}
        className="space-y-4"
      >
        <Field label="Title">
          <input
            required
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            className="input"
            placeholder="Short summary of the issue"
          />
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Source type">
            <select
              value={form.source_type}
              onChange={(e) => setForm({ ...form, source_type: e.target.value as any })}
              className="input"
            >
              {SOURCE_TYPES.map((s) => (
                <option key={s} value={s}>
                  {SOURCE_LABEL[s] ?? s}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Severity">
            <select
              value={form.severity}
              onChange={(e) => setForm({ ...form, severity: e.target.value as any })}
              className="input"
            >
              {SEVERITIES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <Field label="Source reference (post ID, ticket ID, URL, etc.)">
          <input
            value={form.source_ref}
            onChange={(e) => setForm({ ...form, source_ref: e.target.value })}
            className="input"
            placeholder="Optional"
          />
        </Field>
        <Field label="Description">
          <textarea
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            className="input min-h-[120px]"
            placeholder="What was observed? What initial action was taken?"
          />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="btn-secondary">
            Cancel
          </button>
          <button type="submit" disabled={m.isPending} className="btn-primary">
            {m.isPending ? "Creating…" : "Create case"}
          </button>
        </div>
      </form>
      <StyleHelpers />
    </Modal>
  );
}

// ============= Detail modal =============

function CaseDetailModal({
  caseId,
  onClose,
  onChange,
}: {
  caseId: string;
  onClose: () => void;
  onChange: () => void;
}) {
  const qc = useQueryClient();
  const get = useServerFn(getEscalationCase);
  const addNote = useServerFn(addEscalationNote);
  const escalate = useServerFn(escalateCase);
  const close = useServerFn(closeEscalationCase);
  const reopen = useServerFn(reopenEscalationCase);

  const { data, isLoading } = useQuery({
    queryKey: ["admin", "escalation-case", caseId],
    queryFn: () => get({ data: { id: caseId } }),
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["admin", "escalation-case", caseId] });
    onChange();
  };

  const [note, setNote] = useState("");
  const [escalateReason, setEscalateReason] = useState("");
  const [closeOutcome, setCloseOutcome] = useState("");
  const [reopenReason, setReopenReason] = useState("");

  const noteM = useMutation({
    mutationFn: () => addNote({ data: { id: caseId, note } }),
    onSuccess: () => {
      setNote("");
      toast.success("Note added");
      refresh();
    },
    onError: (e: any) => toast.error(e?.message ?? "Failed"),
  });

  const escalateM = useMutation({
    mutationFn: () => escalate({ data: { id: caseId, reason: escalateReason } }),
    onSuccess: () => {
      setEscalateReason("");
      toast.success("Escalated");
      refresh();
    },
    onError: (e: any) => toast.error(e?.message ?? "Failed"),
  });

  const closeM = useMutation({
    mutationFn: () => close({ data: { id: caseId, outcome: closeOutcome } }),
    onSuccess: () => {
      setCloseOutcome("");
      toast.success("Case closed");
      refresh();
    },
    onError: (e: any) => toast.error(e?.message ?? "Failed"),
  });

  const reopenM = useMutation({
    mutationFn: () => reopen({ data: { id: caseId, reason: reopenReason } }),
    onSuccess: () => {
      setReopenReason("");
      toast.success("Case reopened");
      refresh();
    },
    onError: (e: any) => toast.error(e?.message ?? "Failed"),
  });

  if (isLoading || !data) {
    return (
      <Modal title="Loading case…" onClose={onClose}>
        <div className="text-sm text-muted-foreground">Loading…</div>
      </Modal>
    );
  }

  const c = data.case;
  const isClosed = c.status === "closed";
  const atFinal = c.current_stage >= 3;

  return (
    <Modal title={c.title} onClose={onClose} wide>
      <div className="space-y-5">
        <div className="flex flex-wrap items-center gap-2">
          <Badge className={STATUS_STYLES[c.status] ?? STATUS_STYLES.open}>
            {c.status.replace("_", " ")}
          </Badge>
          <Badge className={SEVERITY_STYLES[c.severity] ?? SEVERITY_STYLES.medium}>
            {c.severity}
          </Badge>
          <Badge className="bg-card text-foreground border-gold/20">
            Stage {c.current_stage}: {STAGE_LABEL[c.current_stage]}
          </Badge>
          <Badge className="bg-card text-foreground border-gold/20">
            {SOURCE_LABEL[c.source_type] ?? c.source_type}
          </Badge>
        </div>

        {c.description && (
          <div className="rounded-xl border border-gold/15 bg-card p-3 text-sm text-foreground/90 whitespace-pre-wrap">
            {c.description}
          </div>
        )}
        <div className="grid gap-2 text-xs text-muted-foreground sm:grid-cols-2">
          <div>Opened: {fmt(c.created_at)}</div>
          {c.source_ref && <div>Reference: {c.source_ref}</div>}
          {c.closed_at && <div>Closed: {fmt(c.closed_at)}</div>}
          {c.closure_outcome && <div className="sm:col-span-2">Outcome: {c.closure_outcome}</div>}
        </div>

        {/* Actions */}
        {!isClosed && (
          <div className="space-y-3 rounded-xl border border-gold/20 bg-gold/5 p-4">
            <h4 className="text-sm font-semibold text-foreground">Take action</h4>

            <div className="space-y-2">
              <label className="text-xs font-medium text-muted-foreground">Add a note</label>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                className="input min-h-[70px]"
                placeholder="Record an observation, decision, or update"
              />
              <button
                onClick={() => noteM.mutate()}
                disabled={!note.trim() || noteM.isPending}
                className="btn-secondary inline-flex items-center gap-2 text-xs"
              >
                <MessageSquare className="h-3.5 w-3.5" /> Add note
              </button>
            </div>

            {!atFinal && (
              <div className="space-y-2 border-t border-gold/20 pt-3">
                <label className="text-xs font-medium text-muted-foreground">
                  Escalate to {STAGE_LABEL[c.current_stage + 1]}
                </label>
                <textarea
                  value={escalateReason}
                  onChange={(e) => setEscalateReason(e.target.value)}
                  className="input min-h-[60px]"
                  placeholder="Reason for escalation (required)"
                />
                <button
                  onClick={() => escalateM.mutate()}
                  disabled={!escalateReason.trim() || escalateM.isPending}
                  className="inline-flex items-center gap-2 rounded-full bg-orange-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-orange-700 disabled:opacity-50"
                >
                  <ArrowUpCircle className="h-3.5 w-3.5" /> Escalate to Stage {c.current_stage + 1}
                </button>
              </div>
            )}

            <div className="space-y-2 border-t border-gold/20 pt-3">
              <label className="text-xs font-medium text-muted-foreground">
                Close case with documented outcome
              </label>
              <textarea
                value={closeOutcome}
                onChange={(e) => setCloseOutcome(e.target.value)}
                className="input min-h-[60px]"
                placeholder="Final outcome / actions taken (required)"
              />
              <button
                onClick={() => closeM.mutate()}
                disabled={!closeOutcome.trim() || closeM.isPending}
                className="inline-flex items-center gap-2 rounded-full bg-emerald-700 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-800 disabled:opacity-50"
              >
                <CheckCircle2 className="h-3.5 w-3.5" /> Close case
              </button>
            </div>
          </div>
        )}

        {isClosed && (
          <div className="space-y-2 rounded-xl border border-gold/20 bg-card p-4">
            <h4 className="text-sm font-semibold text-foreground">Reopen case</h4>
            <textarea
              value={reopenReason}
              onChange={(e) => setReopenReason(e.target.value)}
              className="input min-h-[60px]"
              placeholder="Reason for reopening"
            />
            <button
              onClick={() => reopenM.mutate()}
              disabled={!reopenReason.trim() || reopenM.isPending}
              className="inline-flex items-center gap-2 rounded-full border border-gold/30 bg-card px-3 py-1.5 text-xs font-semibold text-foreground hover:bg-gold/10 disabled:opacity-50"
            >
              <RotateCcw className="h-3.5 w-3.5" /> Reopen
            </button>
          </div>
        )}

        {/* Audit trail */}
        <div>
          <h4 className="mb-2 text-sm font-semibold text-foreground">Audit trail</h4>
          <ol className="space-y-2">
            {data.actions.map((a: any) => (
              <li
                key={a.id}
                className="rounded-xl border border-gold/15 bg-card p-3 text-sm"
              >
                <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                  <Clock className="h-3 w-3" /> {fmt(a.created_at)}
                  <span className="rounded-full bg-gold/10 px-2 py-0.5 font-medium text-foreground">
                    {a.action}
                  </span>
                  <span>
                    Stage {a.stage} — {STAGE_LABEL[a.stage] ?? a.role}
                  </span>
                </div>
                {a.decision_summary && (
                  <p className="mt-1.5 whitespace-pre-wrap text-foreground/90">
                    {a.decision_summary}
                  </p>
                )}
              </li>
            ))}
          </ol>
        </div>
      </div>
      <StyleHelpers />
    </Modal>
  );
}

// ============= Shared =============

function Modal({
  title,
  children,
  onClose,
  wide,
}: {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
  wide?: boolean;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4 backdrop-blur-sm">
      <div
        className={`my-8 w-full ${
          wide ? "max-w-3xl" : "max-w-xl"
        } rounded-2xl border border-gold/30 bg-card p-6 shadow-2xl`}
      >
        <div className="mb-4 flex items-start justify-between gap-3">
          <h2 className="font-display text-lg text-foreground">{title}</h2>
          <button
            onClick={onClose}
            className="rounded-lg p-1 text-foreground/60 hover:bg-gold/10 hover:text-foreground"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}

function StyleHelpers() {
  return (
    <style>{`
      .input { width:100%; border-radius:0.75rem; border:1px solid hsl(var(--border)); background:hsl(var(--card)); padding:0.55rem 0.75rem; font-size:0.875rem; color:hsl(var(--foreground)); }
      .input:focus { outline:none; border-color:var(--gold-deep, #b8860b); box-shadow:0 0 0 2px rgba(184,134,11,0.2); }
      .btn-primary { display:inline-flex; align-items:center; gap:0.5rem; border-radius:9999px; background:var(--gold, #b8860b); padding:0.5rem 1rem; font-size:0.875rem; font-weight:600; color:#fff; }
      .btn-primary:hover { opacity:0.9; }
      .btn-primary:disabled { opacity:0.5; cursor:not-allowed; }
      .btn-secondary { display:inline-flex; align-items:center; gap:0.5rem; border-radius:9999px; border:1px solid rgba(184,134,11,0.3); background:hsl(var(--card)); padding:0.5rem 1rem; font-size:0.875rem; font-weight:500; color:hsl(var(--foreground)); }
      .btn-secondary:hover { background:rgba(184,134,11,0.08); }
    `}</style>
  );
}
